import type { DeliveryPin, Order, OrderBill, OrderBillCharge, OrderBillLine, RawOrderBill, RawOrderBillCharge, RawOrderBillLine, OrderStatus, FulfillmentType, RawOrder } from "../../../interfaces/OrderObject";
import { extractApiErrorMessage } from "../../helpers/ApiErrorHelper";

const KNOWN_STATUSES: OrderStatus[] = ["NOT_YET_RESPONDED", "ACCEPTED", "REJECTED"];

/** Coerce an arbitrary status string to a known OrderStatus, defaulting safely. */
function toOrderStatus(value: unknown): OrderStatus {
  return KNOWN_STATUSES.includes(value as OrderStatus)
    ? (value as OrderStatus)
    : "NOT_YET_RESPONDED";
}

/** Coerce a fulfillment string; default to DELIVERY only if a pin/address is present, else ON_PREMISE. */
function toFulfillmentType(value: unknown, r: Record<string, unknown>): FulfillmentType {
  if (value === "DELIVERY" || value === "ON_PREMISE" || value === "DINE_IN") return value;
  // Backend may omit it on older records - infer from delivery details.
  const hasDelivery = r.deliveryAddress != null || r.deliveryPin != null;
  return hasDelivery ? "DELIVERY" : "ON_PREMISE";
}

/** Normalize a delivery pin from either lat/lng or latitude/longitude. */
function toDeliveryPin(value: unknown): DeliveryPin | null {
  if (!value || typeof value !== "object") return null;
  const p = value as Record<string, unknown>;
  const lat = (p.lat ?? p.latitude) as number | undefined;
  const lng = (p.lng ?? p.longitude) as number | undefined;
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  return { lat, lng };
}

function toOrderBillLine(l: RawOrderBillLine): OrderBillLine {
  return {
    productId: l.productId ?? l.product_id,
    productName: l.productName ?? l.product_name ?? l.name ?? "Item",
    variantLabel: l.variantLabel ?? l.variant_label,
    quantity: Number(l.quantity ?? 0),
    unitPrice: l.unitPrice ?? l.unit_price,
    lineTotal: Number(l.lineTotal ?? l.line_total ?? 0),
  };
}

function toOrderBillCharge(c: RawOrderBillCharge): OrderBillCharge {
  return { label: c.label ?? c.name ?? "Charge", amount: Number(c.amount ?? 0) };
}

/** Normalize the `bill` jsonb; returns undefined when absent or malformed. */
function toOrderBill(value: RawOrderBill | null | undefined): OrderBill | undefined {
  if (!value || typeof value !== "object") return undefined;
  const table = value.tableNumber ?? value.table_number;
  return {
    tableNumber: table != null ? String(table) : undefined,
    lines: (value.lines ?? []).map(toOrderBillLine),
    charges: (value.charges ?? []).map(toOrderBillCharge),
    subtotal: value.subtotal,
    total: value.total,
  };
}

/**
 * Convert one raw order to the UI shape. Defensive about camelCase/snake_case
 * (mirrors businessConvertor/productConvertor) so it survives backend renames.
 */
export function convertRawOrder(raw: RawOrder): Order {
  const r = raw as unknown as Record<string, unknown>;
  return {
    orderId: (r.orderId ?? r.order_id) as string,
    businessId: (r.businessId ?? r.business_id) as string,
    buyerId: (r.buyerId ?? r.buyer_id) as string,
    // Absent (not "") when empty, matching Order.collectionUrl?: dine-in orders have none.
    collectionUrl: ((r.collectionUrl ?? r.collection_url) as string | undefined) || undefined,
    totalPrice: ((r.totalPrice ?? r.total_price) as number | undefined) ?? 0,
    status: toOrderStatus(r.status),
    fulfillmentType: toFulfillmentType(r.fulfillmentType ?? r.fulfillment_type, r),
    dineInSessionId: ((r.dineInSessionId ?? r.dine_in_session_id) as string | null | undefined) ?? undefined,
    bill: toOrderBill(raw.bill),
    buyerPhone: (r.buyerPhone ?? r.buyer_phone) as string | undefined,
    deliveryAddress: ((r.deliveryAddress ?? r.delivery_address) as string | null | undefined) ?? null,
    deliveryPin: toDeliveryPin(r.deliveryPin ?? r.delivery_pin),
    respondedBy: ((r.respondedBy ?? r.responded_by) as string | null | undefined) ?? null,
    respondedByName: ((r.respondedByName ?? r.responded_by_name) as string | null | undefined) ?? null,
    createdAt: (r.createdAt ?? r.created_at) as string,
    updatedAt: (r.updatedAt ?? r.updated_at) as string | undefined,
  };
}

/**
 * Extract the order list from a response envelope. Tolerates both shapes:
 * aligned standard envelope (contents as array) and the original contract
 * (contents.orders). Returns [] for a successful-but-empty result.
 */
export function convertRawOrderList(data: unknown): Order[] {
  const d = (data || {}) as Record<string, any>;
  const contents = d.contents;
  let rawList: unknown;
  if (Array.isArray(contents)) {
    rawList = contents;
  } else if (contents && typeof contents === "object") {
    rawList = contents.orders;
  }
  if (!Array.isArray(rawList)) return [];
  return (rawList as RawOrder[]).map(convertRawOrder);
}

/**
 * Extract a single order from a response envelope (create-order / respond-to-order).
 * Tolerates contents-as-array (aligned) and contents-as-object (original contract).
 * Returns null when no order is present.
 */
export function convertRawOrderEnvelope(data: unknown): Order | null {
  const d = (data || {}) as Record<string, any>;
  const contents = d.contents;
  if (!contents) return null;
  const raw = Array.isArray(contents) ? contents[0] : contents;
  if (!raw || typeof raw !== "object") return null;
  return convertRawOrder(raw as RawOrder);
}

// --- Error-envelope reading ---------------------------------------------------
// Single place that understands the orders contract's errorInformation object
// shape (errorCode/errorMessage) AND the app-standard envelope, so the two
// never diverge as the backend aligns (A1).

/** True when the envelope represents a business/processing error. */
export function orderEnvelopeHasError(data: unknown): boolean {
  const d = (data || {}) as Record<string, any>;
  const info = d.errorInformation;
  if (info) {
    if (Array.isArray(info)) return info.length > 0;
    if (info.errorCode || info.errorMessage || info.errorDetail) return true;
  }
  const status = d.generalProcessingStatus?.processingStatus;
  return !!(status && status !== "OK");
}

/** Human-facing message for an order error envelope, with a safe fallback. */
export function extractOrderError(data: unknown): string {
  const d = (data || {}) as Record<string, any>;
  const info = d.errorInformation;
  if (info && !Array.isArray(info) && (info.errorMessage || info.errorCode)) {
    return info.errorMessage || info.errorCode;
  }
  return extractApiErrorMessage(data) || "Something went wrong. Please try again.";
}

/** The error code (if any) - lets callers branch on ORDER_NOT_ALLOWED, ACTIVE_ORDER_EXISTS, etc. */
export function extractOrderErrorCode(data: unknown): string | null {
  const d = (data || {}) as Record<string, any>;
  const info = d.errorInformation;
  if (!info) return null;
  if (Array.isArray(info)) return info[0]?.errorCode ?? null;
  return info.errorCode ?? null;
}
